/**
 * The decisions behind the financial profile: which numbers a chart can honestly
 * draw, and what to fall back to when the obvious source is blank.
 *
 * React-free on purpose. EMTA leaves whole columns empty for companies that do
 * not declare turnover (non-profits, businesses under the VAT threshold), and
 * every "the chart is empty" report on this page traces back to one bug: a
 * missing figure was drawn as a zero. Roughly one mirrored company in eight has
 * no EMTA turnover in any year, so this is the normal case, not an edge case.
 * Keeping the rules here means they can be tested without a DOM.
 */
import type { ArQuarter, ArReport, ArReportActivity, ArTaxData } from "../../../../lib/ariregister";

/** The translator, threaded down from the page rather than re-read per component. */
export type T = (key: string, vars?: Record<string, string | number>) => string;

/** One bar. `value` is euros, because that is what Recharts plots. */
export interface SeriesPoint {
  label: string;
  value: number;
}

/** Quarter cells carry cents as strings (that is how BigInt survives JSON). */
export function quarterCents(v: string | null | undefined): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Net margin in percent, or null when there is no turnover to divide by. */
export function marginPct(profit: number | null, revenue: number | null): number | null {
  if (profit == null || revenue == null || revenue === 0) return null;
  return (profit / revenue) * 100;
}

// ---------------------------------------------------------------- yearly trend

/** Which source the yearly trend ended up drawing. The title has to say so. */
export type TrendSource = "emta" | "report";

export interface RevenueTrend {
  source: TrendSource;
  points: SeriesPoint[];
}

/** A decade is as far back as the mirror is useful; older bars only shrink the rest. */
const MAX_TREND_YEARS = 10;

function yearPoints(rows: { year: number; revenueCents: number | null }[]): SeriesPoint[] {
  return rows
    .filter((r) => r.revenueCents != null)
    .sort((a, b) => a.year - b.year)
    .slice(-MAX_TREND_YEARS)
    .map((r) => ({ label: String(r.year), value: (r.revenueCents ?? 0) / 100 }));
}

/**
 * Yearly turnover trend. EMTA is preferred because it is the fresher source,
 * but it publishes turnover only for companies that declare it; when it holds
 * fewer than two usable years the annual reports take over. A company can carry
 * seven reported years and not a single EMTA turnover cell, and before this
 * fallback that company's page showed no trend at all.
 */
export function revenueTrend(tax: ArTaxData[], reports: ArReport[]): RevenueTrend | null {
  const emta = yearPoints(tax);
  if (emta.length >= 2) return { source: "emta", points: emta };
  const report = yearPoints(reports);
  if (report.length >= 2) return { source: "report", points: report };
  return null;
}

// -------------------------------------------------------------- quarterly bars

/** What a quarter chart is plotting. EMTA gives one or the other, rarely both. */
export type QuarterMetric = "revenue" | "stateTax";

export interface QuarterSeries {
  year: number;
  metric: QuarterMetric;
  points: SeriesPoint[];
}

function metricPoints(qs: ArQuarter[], field: "revenueCents" | "stateTaxCents"): SeriesPoint[] {
  return qs
    .filter((q) => quarterCents(q[field]) != null)
    .sort((a, b) => a.q - b.q)
    .map((q) => ({ label: `Q${q.q}`, value: (quarterCents(q[field]) ?? 0) / 100 }));
}

/**
 * One year's quarter bars: turnover when EMTA published it, otherwise the state
 * taxes paid. Taxes are published for everyone with a payroll and are the next
 * best read on a company's size, so a page that would have shown an empty frame
 * now shows a real one - with a title that names the metric, since a tax bar and
 * a turnover bar are not the same claim. Quarters with no figure are dropped
 * rather than flattened to zero.
 */
export function quarterSeries(row: ArTaxData | null | undefined): QuarterSeries | null {
  const qs = row?.quarters ?? [];
  if (!row || qs.length === 0) return null;
  const revenue = metricPoints(qs, "revenueCents");
  if (revenue.length > 0) return { year: row.year, metric: "revenue", points: revenue };
  const stateTax = metricPoints(qs, "stateTaxCents");
  if (stateTax.length > 0) return { year: row.year, metric: "stateTax", points: stateTax };
  return null;
}

/**
 * The newest year worth charting on its own. The running year is usually half
 * published (Q1 and Q2 by August), which is still two bars; a year down to one
 * bar is skipped in favour of the last one that has more, because a single
 * column is a number pretending to be a chart.
 */
export function latestQuarterSeries(tax: ArTaxData[], minPoints = 2): QuarterSeries | null {
  for (const row of [...tax].sort((a, b) => b.year - a.year)) {
    const s = quarterSeries(row);
    if (s && s.points.length >= minPoints) return s;
  }
  return null;
}

/**
 * True when the mirror holds EMTA rows for this company but not one turnover
 * figure among them. The page says so out loud - otherwise the missing turnover
 * chart reads as a broken page rather than as data the state never published.
 */
export function emtaHidesRevenue(tax: ArTaxData[]): boolean {
  if (tax.length === 0) return false;
  return tax.every(
    (r) => r.revenueCents == null && (r.quarters ?? []).every((q) => quarterCents(q.revenueCents) == null),
  );
}

// ------------------------------------------------------------ per-year details

/** Report figures the summary table has no column for, in the order they read. */
export const EXTRA_REPORT_FIELDS = [
  "profitBeforeTaxCents",
  "laborCostCents",
  "currentAssetsCents",
  "cashCents",
  "currentLiabilitiesCents",
  "nonCurrentLiabilitiesCents",
] as const;

export type ExtraReportField = (typeof EXTRA_REPORT_FIELDS)[number];

export interface YearFigure {
  field: ExtraReportField;
  cents: number;
}

/** Everything the table can unfold under one year, once it is known to be worth unfolding. */
export interface YearDetail {
  year: number;
  figures: YearFigure[];
  /** EMTA quarters for the same year, when the mirror holds them. */
  quarters: QuarterSeries | null;
  /** Turnover split by activity for the same year, largest first. */
  activities: ArReportActivity[];
  periodEnd: string | null;
  submittedAt: string | null;
  category: string | null;
  isConsolidated: boolean;
}

/**
 * What sits behind one year of the report table, or null when nothing does.
 *
 * The null matters as much as the payload: a row with nothing underneath must
 * not look clickable, so the table asks this question before it draws the
 * chevron rather than opening an empty drawer and letting the user find out.
 */
export function yearDetail(
  report: ArReport,
  tax: ArTaxData[],
  activities: ArReportActivity[],
): YearDetail | null {
  const figures: YearFigure[] = [];
  for (const field of EXTRA_REPORT_FIELDS) {
    const cents = report[field];
    if (cents != null) figures.push({ field, cents });
  }

  const detail: YearDetail = {
    year: report.year,
    figures,
    quarters: quarterSeries(tax.find((r) => r.year === report.year)),
    activities: activities
      .filter((a) => a.year === report.year && a.revenueCents != null && a.revenueCents > 0)
      .sort((a, b) => (b.revenueCents ?? 0) - (a.revenueCents ?? 0)),
    periodEnd: report.periodEnd,
    submittedAt: report.submittedAt,
    category: report.category,
    isConsolidated: report.isConsolidated === true,
  };

  const hasAnything =
    detail.figures.length > 0 ||
    detail.quarters != null ||
    detail.activities.length > 0 ||
    detail.periodEnd != null ||
    detail.submittedAt != null ||
    detail.category != null ||
    detail.isConsolidated;

  return hasAnything ? detail : null;
}
